'use client';
import React from 'react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { registerOTPSchema } from '@/lib/validations/restaurant/vendorSchemas';
import client from '@/clients/common/client';
import { handleErrors } from '@/lib/helpers/functions';
import TermsAndCondition from '@/components/shared/TermsAndCondition';
import InputField from '@/components/shared/InputField';
import { Arrow2 } from '@/components/ui/Icons';
import Button from '@ui/Button';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import toast from 'react-hot-toast';
import { storeFront } from '@/lib/utils/routeLinks';
import useRestaurantLocale from '@/hooks/useRestaurantLocale';

type FormData = z.infer<ReturnType<typeof registerOTPSchema>>;

export default function VendorGetOTPForm() {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const locale = useParams().locale;
  const { restaurantLang } = useRestaurantLocale();
  const searchParams = useSearchParams();
  const searchParamsRedirect = searchParams.get('redirect');

  const router = useRouter();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(registerOTPSchema(restaurantLang as 'de' | 'en')),
    mode: 'onTouched',
    defaultValues: {
      contact: {
        email: '',
      },
    },
  });

  const t = useTranslations();

  const onSubmit: SubmitHandler<FormData> = async (data, event) => {
    event?.preventDefault();
    setIsLoading(true);

    const payload: any = {
      contact: data.contact,
    };

    const token = await executeRecaptcha?.('register_restaurant');
    payload['g-recaptcha-response'] = token;

    client
      .post('/v1/restaurant/register', payload)
      .then((res) => {
        if (res.status === 200) {
          let nextLink =
            `${storeFront.registerBusiness.de()}/otp` +
            '?' +
            `email=${encodeURIComponent(data.contact.email)}`;
          if (searchParamsRedirect) {
            nextLink =
              nextLink +
              `&redirect=${encodeURIComponent(searchParamsRedirect)}`;
          }
          const digistoreOrderId = searchParams.get('digistore_order_id');
          const shaSign = searchParams.get('sha_sign');
          const digistoreProductPlanId = searchParams.get(
            'digistore_product_plan_id'
          );
          if (digistoreOrderId && digistoreProductPlanId && shaSign) {
            nextLink += `&digistore_order_id=${digistoreOrderId}&digistore_product_plan_id=${digistoreProductPlanId}&sha_sign=${shaSign}`;
          }
          router.push(nextLink);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        handleErrors({ type: 'form', setError, errors: error.response });
        toast.error(error?.response?.data?.message ?? 'Error');
      });
  };

  return (
    <form
      className="mb-6 w-full 2xl:mb-[30px]"
      onSubmit={handleSubmit(onSubmit)}
    >
      <InputField<FormData>
        name="contact.email"
        type="email"
        label={t('email')}
        placeholder="sample@name.com"
        register={register}
        errors={errors}
      />

      <Button type="submit" gradient disabled={!isValid} className="group">
        {isLoading && (
          <span className="mr-[5px]">
            <LoadingSpinner />
          </span>
        )}
        <span className="mr-[14px]">{t('next')}</span>
        {!isLoading && <Arrow2 />}
      </Button>
      <TermsAndCondition
        btnText={t('next')}
        lang={locale === 'en-DE' ? 'en' : 'de'}
      />
    </form>
  );
}
